import { useMemo, useState } from 'react';
import { Scale } from 'lucide-react';
import { Hint } from '@/components/Hint';
import { cn } from '@/lib/utils';
import { statusInk } from '@/lib/status';
import {
  createOrderTask,
  type OpenAgentTask,
  type OrdersOverview,
  type OverviewReceiving,
} from '@/api/orders';
import { BalanceReport } from './BalanceReport';
import { Can } from '@/auth/guards';
import { money } from './lib';
import {
  AcotReceivingLink, JourneyName, PendingNudge, RowAction,
  WorkEmpty, WorkFootnote, WorkRow, WorkSection,
} from './workLib';

/**
 * The balancing queue, ready first: report inline, the acot receiving-edit
 * grid one click away, and the close-out recorded as a confirm_balanced nudge —
 * the run re-mirrors and marks, so "balanced" stays a verified fact rather
 * than a claim (the mark endpoint refuses if the mirror still shows edits).
 */
export function BalanceView({ overview, tasks, onChanged, onOpenJourney }: {
  overview: OrdersOverview;
  tasks: OpenAgentTask[];
  onChanged: () => void;
  onOpenJourney: (orderId: string) => void;
}) {
  const [openReportId, setOpenReportId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const confirmTaskByReceipt = useMemo(() => {
    const map = new Map<string, OpenAgentTask>();
    for (const t of tasks) {
      if (t.kind === 'confirm_balanced' && t.payload.receipt_id != null) {
        map.set(String(t.payload.receipt_id), t);
      }
    }
    return map;
  }, [tasks]);

  const { ready, blocked, accepted, balancedCount } = useMemo(() => {
    const open = overview.receivings.filter((r) => !r.balancedAt);
    return {
      ready: open.filter((r) => r.ready && r.blockers.length === 0)
        .sort((a, b) => (b.costEditLines ?? 0) - (a.costEditLines ?? 0)),
      blocked: open.filter((r) => !(r.ready && r.blockers.length === 0) && !r.accepted)
        .sort((a, b) => b.receivingId - a.receivingId),
      // Header-only vendors: not workable, not outstanding — one quiet line.
      accepted: open.filter((r) => !(r.ready && r.blockers.length === 0) && r.accepted),
      balancedCount: overview.receivings.filter((r) => r.balancedAt).length,
    };
  }, [overview]);

  if (ready.length === 0 && blocked.length === 0) {
    return <WorkEmpty>Every mirrored receiving is balanced.</WorkEmpty>;
  }

  const row = (rcv: OverviewReceiving, isReady: boolean) => {
    const confirming = confirmTaskByReceipt.get(rcv.receiptId);
    const expanded = openReportId === rcv.receiptId;
    return (
      <WorkRow
        key={rcv.receiptId}
        scrollKey={`receiving:${rcv.receiptId}`}
        title={<JourneyName vendor={rcv.vendor} label={rcv.label} onOpen={() => onOpenJourney(rcv.orderId)} />}
        className={cn(!isReady && 'opacity-80')}
        expanded={expanded}
        onToggle={() => setOpenReportId(expanded ? null : rcv.receiptId)}
        detail={expanded ? <BalanceReport receiptId={rcv.receiptId} currency={rcv.currency} /> : undefined}
        actions={
          confirming ? (
            <PendingNudge task={confirming} onWithdrawn={onChanged} />
          ) : isReady && (
            <Can code="orders:write:nudges">
              <Hint content="Queues the close-out; the next run re-mirrors the receiving and marks it balanced.">
                <RowAction
                  icon={<Scale className="h-3 w-3" />}
                  busy={busyId === rcv.receiptId}
                  disabled={busyId === rcv.receiptId}
                  onClick={async () => {
                    setBusyId(rcv.receiptId);
                    try {
                      await createOrderTask(rcv.orderId, {
                        kind: 'confirm_balanced',
                        payload: { receipt_id: rcv.receiptId },
                        note: `Cost edits applied in acot receiving #${rcv.receivingId}`,
                      });
                      onChanged();
                    } finally {
                      setBusyId(null);
                    }
                  }}
                >
                  Edits applied
                </RowAction>
              </Hint>
            </Can>
          )
        }
      >
        <AcotReceivingLink receivingId={rcv.receivingId} className="text-xs" />

        {isReady ? (
          <span className="inline-flex items-center gap-2 text-xs tabular-nums text-muted-foreground">
            <Hint content="Booked in acot-db now → target after cost edits">
              <span>
                {money(rcv.bookedTotal, rcv.currency)} → <span className="font-medium text-foreground">{money(rcv.targetTotal, rcv.currency)}</span>
              </span>
            </Hint>
            {rcv.costDeltaTotal != null && rcv.costDeltaTotal !== 0 && (
              // Money direction is not a state: plain text, the sign says it.
              <span>
                {rcv.costDeltaTotal > 0 ? '+' : ''}{money(rcv.costDeltaTotal, rcv.currency)}
              </span>
            )}
            <span>{rcv.costEditLines} edit{rcv.costEditLines === 1 ? '' : 's'}</span>
          </span>
        ) : (
          // flex-1 + min-w-0: takes whatever width remains and truncates
          // there, instead of wrapping the whole span onto its own line.
          <Hint content={[...rcv.needs.map((n) => n.text), ...rcv.blockers.map((b) => b.text)].join(' · ') || undefined}>
            <span className={cn('min-w-0 flex-1 truncate text-xs', statusInk('warning'))}>
              {rcv.needs[0]?.text || rcv.blockers[0]?.text || 'not ready'}
              {rcv.needs.length + rcv.blockers.length > 1 && ` (+${rcv.needs.length + rcv.blockers.length - 1} more)`}
            </span>
          </Hint>
        )}
      </WorkRow>
    );
  };

  return (
    <div>
      <WorkSection title="Ready" count={ready.length}>
        {ready.length === 0
          ? <WorkEmpty compact>Nothing ready right now.</WorkEmpty>
          : ready.map((rcv) => row(rcv, true))}
      </WorkSection>

      {blocked.length > 0 && (
        <WorkSection title="Blocked" count={blocked.length}>
          {blocked.map((rcv) => row(rcv, false))}
        </WorkSection>
      )}

      {(balancedCount > 0 || accepted.length > 0) && (
        <WorkFootnote>
          {[
            balancedCount > 0 ? `${balancedCount} receiving${balancedCount === 1 ? '' : 's'} balanced` : null,
            accepted.length > 0
              ? `${accepted.length} header-only by design (${accepted.map((r) => `#${r.receivingId}`).join(', ')})`
              : null,
          ].filter(Boolean).join(' · ')}.
        </WorkFootnote>
      )}
    </div>
  );
}
